import { BATTLE_MINI_TUTORIAL, BATTLE_TUTORIAL, type BattleTutorial } from 'features/battle/tutorial';
import { parseJson } from 'shared/lib/json';

const STORAGE_KEY = 'tb-battle-tutorial-done';

/** Either lesson unlocks Battle Badges and Arena. `first-battle` grandfathers people who already finished the old one. */
const UNLOCK_TUTORIAL_IDS = [BATTLE_MINI_TUTORIAL.id, BATTLE_TUTORIAL.id] as const;

const readCompletedTutorials = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const value = parseJson(raw);
    return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
};

const TUTORIAL_CHANGE_EVENT = 'tb-battle-tutorial-change';

const writeCompletedTutorials = (ids: readonly string[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore quota / private mode */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TUTORIAL_CHANGE_EVENT));
  }
};

export const subscribeTutorialProgress = (onChange: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(TUTORIAL_CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(TUTORIAL_CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

export const isTutorialDone = (id: string): boolean => readCompletedTutorials().includes(id);

export const isTutorialComplete = (): boolean => UNLOCK_TUTORIAL_IDS.some((id) => isTutorialDone(id));

export const isTutorialLocked = (tutorial: Pick<BattleTutorial, 'id' | 'requires'>): boolean =>
  tutorial.requires != null && !isTutorialDone(tutorial.requires) && !isTutorialDone(tutorial.id);

export const markTutorialComplete = (id: string = BATTLE_MINI_TUTORIAL.id): void => {
  const current = readCompletedTutorials();
  if (!current.includes(id)) writeCompletedTutorials([...current, id]);
};

export const resetTutorialProgress = (): void => writeCompletedTutorials([]);
